import type { AuditActorType } from '@prisma/client';
import type { BrandLogMetadata } from '@/lib/brand/loggingMetadata';

export type RequestContext = BrandLogMetadata & {
  requestId: string;
  method?: string;
  url?: string;
  userId?: string;
  userRole?: string | null;
  actorType?: AuditActorType;
  ip?: string;
  userAgent?: string;
};

type AsyncStorage<T> = {
  run: <R>(store: T, callback: () => Promise<R>) => Promise<R>;
  getStore: () => T | undefined;
};

let requestContextStorage: AsyncStorage<RequestContext> | null = null;

const canUseAsyncStorage =
  typeof process !== 'undefined' && process.release?.name === 'node';

if (canUseAsyncStorage) {
  try {
    // Keep this invisible to client-side bundlers. Some legacy client imports
    // still reach server utility modules for shared types/classes.
    const nodeRequire = eval('require') as NodeRequire;
    const asyncHooks = nodeRequire(
      'async_hooks'
    ) as typeof import('async_hooks');
    requestContextStorage = new asyncHooks.AsyncLocalStorage<RequestContext>();
  } catch {
    requestContextStorage = null;
  }
}

export const runWithRequestContext = async <T>(
  context: RequestContext,
  fn: () => Promise<T>
): Promise<T> => {
  if (!requestContextStorage) return fn();
  return requestContextStorage.run(context, fn);
};

export const getRequestContext = (): RequestContext | undefined =>
  requestContextStorage?.getStore();
